// Single source of truth for languages and routes.
// This module is also loaded by scripts/prerender.js at build time, so it must
// stay free of imports.

export const LANGUAGES = ['de', 'en'] as const;

export type Language = (typeof LANGUAGES)[number];

/**
 * The default language lives on the bare URLs (/, /organisation). Every other
 * language is served under its own prefix (/en, /en/organisation). That keeps the
 * already indexed URLs in place and makes every page self-canonical.
 */
export const DEFAULT_LANGUAGE: Language = 'de';

/**
 * Die Domain, unter der die Seite gefunden werden soll. Speist Canonical-URLs,
 * hreflang, Open-Graph-Tags und die sitemap.xml.
 *
 * Bewusst die Zieldomain und nicht die Adresse, unter der gerade ausgeliefert
 * wird: Sonst verweisen Vorschau-Deploys Suchmaschinen auf sich selbst. Netlify
 * leitet die .netlify.app-Adresse automatisch hierher um, sobald die Domain
 * eingerichtet ist — bis dahin zeigen die Canonicals schon auf das Ziel.
 *
 * Nicht zu verwechseln mit SITE_URL in scripts/generate-cms-config.js: das ist
 * die Adresse, unter der die Redaktionsoberfläche gerade läuft.
 */
export const SITE_URL = 'https://mekollektiv.com';

export interface RouteDefinition {
  /** Path without language prefix, e.g. '/organisation'. */
  path: string;
  /** Key into SEO_CONTENT. */
  seoPage: string;
  /** Component name, resolved to an element in App.tsx. */
  component: string;
  /**
   * The content file behind this route, for the pages that have one. Routes
   * without it are always served: the start page, which every link falls back
   * to, and the legal pages, which the law requires to be reachable.
   */
  page?: string;
}

/**
 * Route paths stay language-neutral: one path per page, with English served
 * under /en. That keeps every page self-canonical and lets prerender.js emit
 * one static file per language and route without a slug translation table.
 */
export const ROUTES: RouteDefinition[] = [
  { path: '/', seoPage: 'home', component: 'HomePage' },
  { path: '/organisation', seoPage: 'organisation', component: 'OrganisationPage', page: 'organisation' },
  { path: '/mitmachen', seoPage: 'mitmachen', component: 'MitmachenPage', page: 'mitmachen' },
  { path: '/instagram', seoPage: 'instagram', component: 'InstagramPage', page: 'socialMedia' },
  { path: '/podcast', seoPage: 'podcast', component: 'PodcastPage', page: 'podcast' },
  { path: '/impressum', seoPage: 'impressum', component: 'ImpressumPage' },
  { path: '/privacy', seoPage: 'privacy', component: 'PrivacyPage' },
  { path: '/cookies', seoPage: 'cookies', component: 'CookiePage' },
  { path: '/cookie-settings', seoPage: 'cookies', component: 'CookieSettingsPage' },
];

export const isLanguage = (value: string): value is Language =>
  (LANGUAGES as readonly string[]).includes(value);

export const isKnownRoute = (path: string): boolean =>
  ROUTES.some((route) => route.path === path);

/**
 * The URL a route has in a given language.
 * '/organisation' + 'de' -> '/organisation'     (default language, no prefix)
 * '/organisation' + 'en' -> '/en/organisation'
 * '/'      + 'en' -> '/en'
 */
export const localizedPath = (path: string, language: Language): string => {
  const normalized = path === '/' ? '' : path.startsWith('/') ? path : `/${path}`;

  if (language === DEFAULT_LANGUAGE) {
    return normalized || '/';
  }

  return `/${language}${normalized}`;
};

/**
 * Splits a pathname into its language prefix and the bare route path. Paths
 * without a prefix report no language - they are the default language.
 * '/en/organisation' -> { language: 'en', path: '/organisation' }
 * '/organisation'    -> { language: null, path: '/organisation' }
 */
export const stripLanguage = (pathname: string): { language: Language | null; path: string } => {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length > 0 && isLanguage(segments[0])) {
    return {
      language: segments[0],
      path: `/${segments.slice(1).join('/')}`.replace(/\/$/, '') || '/',
    };
  }

  return { language: null, path: pathname.replace(/\/$/, '') || '/' };
};
